import type {
  BaseCartLineConnection,
  BaseCartLineEdge,
  Cart,
  CartBuyerIdentityUpdatePayload,
  CartCreatePayload,
  CartDiscountCodesUpdatePayload,
  CartLine,
  CartLinesAddPayload,
  CartLinesRemovePayload,
  CartLinesUpdatePayload,
  ComponentizableCartLine,
  Product,
  UserError,
  ProductVariant,
} from "@shopify/hydrogen-react/storefront-api-types";
import { mutation, query } from "gql-query-builder";

import { customerFields, moneyFields, userErrorFields } from "./fields";
import {
  availableCountriesMetafield,
  brandVariantIdMetafield,
  noticeMetafield,
  quantityLimitMetafield,
  quantityLimitProductMetafield,
  saleMetafield,
} from "./metafields/product-metafields";
import type { IProductMetafields, IProductVariantMetafields } from "./product-gql";
import { productVariantFragment } from "./fragments";

export const cartDiscountAllocationFragment = {
  operation: "CartCodeDiscountAllocation",
  fields: ["code", { discountedAmount: moneyFields }],
  fragment: true,
};

export const cartCustomDiscountAllocationFragment = {
  operation: "CartCustomDiscountAllocation",
  fields: ["title", { discountedAmount: moneyFields }],
  fragment: true,
};

export const cartAutomaticDiscountAllocationFragment = {
  operation: "CartAutomaticDiscountAllocation",
  fields: ["title", { discountedAmount: moneyFields }],
  fragment: true,
};

export const baseCartLineFields = [
  "id",
  "quantity",
  { attributes: ["key", "value"] },
  {
    cost: [
      { amountPerQuantity: moneyFields },
      { compareAtAmountPerQuantity: moneyFields },
      { subtotalAmount: moneyFields },
      { totalAmount: moneyFields },
    ],
  },
  {
    discountAllocations: [
      cartCustomDiscountAllocationFragment,
      cartDiscountAllocationFragment,
      cartAutomaticDiscountAllocationFragment,
    ],
  },
  {
    merchandise: [
      {
        ...productVariantFragment,
        fields: [
          brandVariantIdMetafield,
          ...productVariantFragment.fields.map((item) =>
            typeof item === "object" && item.product
              ? {
                  product: [
                    ...item.product,
                    quantityLimitMetafield,
                    quantityLimitProductMetafield,
                    noticeMetafield,
                    availableCountriesMetafield,
                    saleMetafield,
                  ],
                }
              : item,
          ),
        ],
      },
    ],
  },
];

export const deliveryGroupFields = [
  "id",
  {
    deliveryOptions: ["title", "deliveryMethodType", { estimatedCost: ["amount"] }],
  },
];

export const lines = {
  operation: "lines(first: 20)",
  fields: [{ edges: [{ node: baseCartLineFields }] }],
  variables: {},
};

export const deliveryGroups = {
  operation: "deliveryGroups(first: 20)",
  fields: [{ edges: [{ node: deliveryGroupFields }] }],
  variables: {},
};

export const cartFields = [
  "id",
  "totalQuantity",
  { attributes: ["key", "value"] },
  {
    buyerIdentity: ["countryCode", "email", "phone", { customer: customerFields }],
  },
  {
    discountAllocations: [
      cartCustomDiscountAllocationFragment,
      cartDiscountAllocationFragment,
      cartAutomaticDiscountAllocationFragment,
    ],
  },
  { discountCodes: ["applicable", "code"] },
  {
    cost: [
      { checkoutChargeAmount: moneyFields },
      { subtotalAmount: moneyFields },
      { totalAmount: moneyFields },
      { totalDutyAmount: moneyFields },
      { totalTaxAmount: moneyFields },
    ],
  },
  "checkoutUrl",
  "createdAt",
  "note",
  "totalQuantity",
  "updatedAt",
  lines,
  deliveryGroups,
];

export interface ICartEdge extends BaseCartLineEdge {
  node: (CartLine | ComponentizableCartLine) & {
    merchandise: ProductVariant &
      IProductVariantMetafields & {
        product: Product & IProductMetafields;
      };
  };
}

/**
 * 카트 정보 조회
 */
export interface ICart {
  cart: Cart & {
    lines: BaseCartLineConnection & {
      edges: ICartEdge[];
    };
  };
}

export const cartQuery = query(
  {
    operation: "cart",
    fields: cartFields,
    variables: { id: { type: "ID!" } },
  },
  undefined,
  { operationName: "Cart" },
);

/**
 * 카트 구매자 정보 업데이트
 */
export interface ICartBuyerIdentityUpdateMutation {
  cartBuyerIdentityUpdate: CartBuyerIdentityUpdatePayload & ICart;
}

export const cartBuyerIdentityUpdateMutation = mutation(
  {
    operation: "cartBuyerIdentityUpdate",
    fields: [{ cart: cartFields }, { userErrors: userErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      buyerIdentity: { type: "CartBuyerIdentityInput!" },
    },
  },
  undefined,
  { operationName: "CartBuyerIdentityUpdate" },
);

/**
 * 카드 생성
 */
export interface ICartCreateMutation {
  cartCreate: CartCreatePayload & ICart;
}

export const cartCreateMutation = mutation(
  {
    operation: "cartCreate",
    fields: [{ cart: cartFields }, { userErrors: userErrorFields }],
  },
  undefined,
  { operationName: "CartCreate" },
);

/**
 * 할인코드 제어
 */
export interface ICartDiscountCodesUpdateMutation {
  cartDiscountCodesUpdate: CartDiscountCodesUpdatePayload & ICart;
}

export const cartDiscountCodesUpdateMutation = mutation(
  {
    operation: "cartDiscountCodesUpdate",
    fields: [{ cart: cartFields }, { userErrors: userErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      discountCodes: { type: "[String!]" },
    },
  },
  undefined,
  { operationName: "CartDiscountCodesUpdate" },
);

/**
 * 카트 아이템 추가
 */
export interface ICartLinesAddMutation {
  cartLinesAdd: CartLinesAddPayload & ICart;
}

export const cartLinesAddMutation = mutation(
  {
    operation: "cartLinesAdd",
    fields: [{ cart: cartFields }, { userErrors: userErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      lines: { type: "[CartLineInput!]!" },
    },
  },
  undefined,
  { operationName: "CartLinesAdd" },
);

/**
 * 카트 아이템 제거
 */
export interface ICartLinesRemoveMutation {
  cartLinesRemove: CartLinesRemovePayload & ICart;
}

export const cartLinesRemoveMutation = mutation(
  {
    operation: "cartLinesRemove",
    fields: [{ cart: cartFields }, { userErrors: userErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      lineIds: { type: "[ID!]!" },
    },
  },
  undefined,
  { operationName: "CartLinesRemove" },
);

/**
 * 카트 아이템 수량 업데이트
 */
export interface ICartLinesUpdateMutation {
  cartLinesUpdate: CartLinesUpdatePayload & ICart;
}

export const cartLinesUpdateMutation = mutation(
  {
    operation: "cartLinesUpdate",
    fields: [{ cart: cartFields }, { userErrors: userErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      lines: { type: "[CartLineUpdateInput!]!" },
    },
  },
  undefined,
  { operationName: "CartLinesUpdate" },
);

/**
 * 카드 추가
 */
export interface ICartCreateInputMutation {
  cartCreate: CartCreatePayload & ICart;
}

export const cartCreateInputMutation = mutation(
  {
    operation: "cartCreate",
    fields: [{ cart: cartFields }, { userErrors: userErrorFields }],
    variables: {
      input: { type: "CartInput!" },
    },
  },
  undefined,
  { operationName: "CartCreateInput" },
);

/**
 * 카트 line item 조회
 */
export interface ICartLineItem {
  cart: {
    lines: BaseCartLineConnection & {
      edges: ICartEdge[];
    };
  };
}

export const cartLineItemQuery = query(
  {
    operation: "cart",
    fields: [
      {
        operation: "lines(first: 20)",
        fields: [
          {
            edges: [
              {
                node: [
                  "id",
                  "quantity",
                  {
                    merchandise: [
                      {
                        operation: "ProductVariant",
                        fields: ["availableForSale", "id", "quantityAvailable", "title"],
                        fragment: true,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
        variables: {},
      },
    ],
    variables: { id: { type: "ID!" } },
  },
  undefined,
  { operationName: "CartLineItem" },
);

export interface ICartAttributesUpdateMutation {
  cartAttributesUpdate: { cart: ICart["cart"]; userErrors: UserError };
}

export const cartAttributesUpdate = mutation(
  {
    operation: "cartAttributesUpdate",
    fields: [{ cart: cartFields }, { userErrors: userErrorFields }],
    variables: {
      cartId: { type: "ID!" },
      attributes: { type: "[AttributeInput!]!" },
    },
  },
  undefined,
  { operationName: "CartAttributesUpdate" },
);
